import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import * as dtos from './apiDtos';
import {
    ISportsBettingBaseApi,
    IGetWebStandardCategoriesParams,
    GetWebStandardCategoriesResponse,
    IGroupedApi,
    createGroupedApi,
    sportsBettingBaseApi,
    IGetWebStandardCategoriesResponse,
    IWebHomeData,
    ISBApiOperationResponse,
    ISBProtobufApiOperationResponse,
    WebHomeData,
    SportsBettingApiResult,
    ILoggingService,
    IConnectionMonitorService,
    IConnectSportsBettingBaseApiParams,
    BetDetailApiDto,
    FavoriteOffers,
    prepareAxiosConfig,
    ISportsBettingSessionApiForDesktop
} from 'eTip--common';
import { IRecaptchaProvider } from 'src/modules/eTipCommon/recaptcha/recaptchaProvider';

import { util } from 'protobufjs';

export * from './apiDtos';

export interface IGetWebStandartEventsParams {
    categoryId?: number;
    gameIds?: number[];
    from?: Date;
    to?: Date;
    searchBy?: string;
    top?: number;
    skip?: number;
}

export interface ISportsBettingApi extends ISportsBettingBaseApi {
    getWebStandardCategoriesAsync: (params: IGetWebStandardCategoriesParams) => Promise<IGetWebStandardCategoriesResponse | null>;
    getWebHomeDataAsync: () => Promise<IWebHomeData>;
    getResultsAsync(): Promise<string>;
    getWeekHitAsync: (type: number) => Promise<ISBApiOperationResponse<BetDetailApiDto>>;
    getPageInfoAsync: (pageName: string) => Promise<dtos.pageInfo.PageInfo>;
    addFavoriteOfferAsync: (id: string, type: number) => Promise<void>;
    deleteFavoriteOfferAsync: (id: string, type: number) => Promise<void>;
    getFavoriteOffersAsync: (includeLive: boolean) => Promise<FavoriteOffers>;
    getDaysWithActiveEvents: () => Promise<string[]>;
}

export enum ReportedProblemType {
    Info = 0,
    Warning = 5,
    Error = 10
}
export enum ErrorReportingIntervalsMs {
    OtherErrors = 300000
}

export function connectSportsBettingGroupedApi(params: IConnectSportsBettingApiParams, loggingService: ILoggingService, connectionMonitorService: IConnectionMonitorService, recaptchaProvider: IRecaptchaProvider): IGroupedApi<ISportsBettingApi> {
    return createGroupedApi(token => {
        return connectSportsBettingApi(params, loggingService, connectionMonitorService, recaptchaProvider, prepareAxiosConfig(token, params.settings.isDebug));
    });
}

export interface IConnectSportsBettingApiParams extends IConnectSportsBettingBaseApiParams {
    sessionApi: ISportsBettingSessionApiForDesktop;
}

export function connectSportsBettingApi(apiParams: IConnectSportsBettingApiParams, loggingService: ILoggingService, connectionMonitorService: IConnectionMonitorService, recaptchaProvider: IRecaptchaProvider, defaultConfig?: AxiosRequestConfig): ISportsBettingApi {
    const baseUrl = `${apiParams.baseUrl}/WebServices/Api/SportsBettingService.svc`;
    const base: ISportsBettingBaseApi = sportsBettingBaseApi(apiParams, loggingService, connectionMonitorService, recaptchaProvider, defaultConfig);
    const dateToString = apiParams.dateTimeFormater.toString;
    const processApiResponse = base.processApiResponse;
    const languageId = apiParams.settings.language.id;

    const toDate = (date: string) => {
        return apiParams.dateTimeFormater.toDate(date);
    };

    const createBuffer = (data: string) => {
        let b = new Uint8Array(util.base64.length(data));
        util.base64.decode(data, b, 0);
        return b;
    };

    return Object.assign(base, {
        getWebStandardCategoriesAsync: (params: IGetWebStandardCategoriesParams) => {
            return new Promise<IGetWebStandardCategoriesResponse | null>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetWebStandardCategories`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            ParentCategoryID: params.parentId,
                            From: params.from && dateToString(params.from),
                            To: params.to && dateToString(params.to),
                            TimeStamp: params.timeStamp,
                            IncludeLiveCategories: params.includeLiveCategories
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBProtobufApiOperationResponse>) => {
                        if (processApiResponse(response, r)) {
                            if (response.data.Result === SportsBettingApiResult.NoChange) {
                                f(null);
                                return;
                            }
                            f(GetWebStandardCategoriesResponse.decode(createBuffer(response.data.ReturnValue)));
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },

        toDate,

        getWebHomeDataAsync: () => {
            return new Promise<IWebHomeData>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetWebHomeData`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBProtobufApiOperationResponse>) => {
                        if (processApiResponse(response, r)) {
                            f(WebHomeData.decode(createBuffer(response.data.ReturnValue)));
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        getResultsAsync: () => {
            return new Promise<string>((f, r) => {
                axios
                    .get(`/ResultsAjax.aspx`, defaultConfig)
                    .then((response: AxiosResponse<string>) => {
                        f(response.data);
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        getPageInfoAsync: (pageName: string) => {
            return new Promise<dtos.pageInfo.PageInfo>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetWebInfo`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            Page: pageName
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBApiOperationResponse<dtos.pageInfo.PageInfo>>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data.ReturnValue);
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        getWeekHitAsync: (type: number): Promise<ISBApiOperationResponse<BetDetailApiDto>> => {
            return new Promise<ISBApiOperationResponse<BetDetailApiDto>>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetWeekHit`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            Type: type
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBApiOperationResponse<BetDetailApiDto>>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data);
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        addFavoriteOfferAsync: (id: string, type: number) => {
            return new Promise<any>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/AddFavoriteOffer`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            OfferId: id, //TODOFAV tipos
                            EventOrCategoryId: id, //TODOFAV MTS
                            Type: type
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<any>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data);
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        deleteFavoriteOfferAsync: (id: string, type: number) => {
            return new Promise<any>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/DeleteFavoriteOffer`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            OfferId: id, //TODOFAV tipos
                            EventOrCategoryId: id, //TODOFAV MTS
                            Type: type
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBApiOperationResponse<any>>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data);
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        getFavoriteOffersAsync: (includeLive: boolean) => {
            return new Promise<FavoriteOffers>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetFavoriteOffers`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token,
                            IncludeLive: includeLive ? true : undefined
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBProtobufApiOperationResponse>) => {
                        if (processApiResponse(response, r)) {
                            f(FavoriteOffers.decode(createBuffer(response.data.ReturnValue)));
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        },
        getDaysWithActiveEvents: () => {
            return new Promise<string[]>((f, r) => {
                axios
                    .post(
                        `${baseUrl}/GetDaysWithActiveEvents`,
                        {
                            LanguageID: languageId,
                            Token: apiParams.token
                        },
                        defaultConfig
                    )
                    .then((response: AxiosResponse<ISBApiOperationResponse<string[]>>) => {
                        if (processApiResponse(response, r)) {
                            f(response.data.ReturnValue);
                        }
                    })
                    .catch(error => {
                        base.processError(error, r);
                    });
            });
        }
    });
}
